// src/pages/CheckPage/EditFailurePage.jsx

import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';

import '../../components/Modals/ModalBase.css';
import './EditFailurePage.css';
import CancelEditingModal from '../../components/Modals/CancelEditingModal';
import SaveChangesModal from '../../components/Modals/SaveChangesModal';
import API_BASE_URL from '../../config';

export default function EditFailurePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const commentRef = useRef(null);

  // form state
  const [site, setSite] = useState('');
  const [dateDetect, setDateDetect] = useState('');
  const [dateClose, setDateClose] = useState('');
  const [reason, setReason] = useState('');
  const [materials, setMaterials] = useState('');
  const [master, setMaster] = useState('');
  const [status, setStatus] = useState('');
  const [comment, setComment] = useState('');

  // select options
  const [siteOptions, setSiteOptions] = useState([]);
  const [reasonOptions, setReasonOptions] = useState([]);

  // loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  // pending navigation path or function
  const [pendingPath, setPendingPath] = useState(null);

  // helper: dd.MM.yyyy ↔ yyyy-MM-dd
  const toDisplay = (d) => (d ? new Date(d).toLocaleDateString('ru-RU') : '');
  const toInputDate = (d) => {
    if (!d) return '';
    const [day, month, year] = d.split('.');
    return `${year}-${month}-${day}`;
  };

  // fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [problemsRes, collectorsRes, reasonsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/breake_problem/list_problems`),
          axios.get(`${API_BASE_URL}/collector/get_all_collectors`),
          axios.get(`${API_BASE_URL}/breake_problem/summary/reasons`),
        ]);

        const record = problemsRes.data.find((p) => p.id === Number(id));
        if (!record) throw new Error('Запись не найдена');

        const cols = collectorsRes.data.map((c) => ({ value: c.id, label: c.name }));
        setSiteOptions(cols);

        const reas = reasonsRes.data.map((r) => ({ value: r.reason, label: r.reason }));
        setReasonOptions(reas);

        // fill form state from record
        setSite(record.collector_id);
        setDateDetect(toInputDate(toDisplay(record.date)));
        setDateClose(record.closing_date ? toInputDate(toDisplay(record.closing_date)) : '');
        setReason(record.reason);
        setMaterials(record.materials || '');
        setMaster(record.master_name || '');
        setStatus(record.status || '');
        setComment(record.comments || '');
      } catch (e) {
        console.error(e);
        setError(e.message || 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // auto-grow textarea
  useEffect(() => {
    if (commentRef.current) {
      commentRef.current.style.height = 'auto';
      commentRef.current.style.height = `${commentRef.current.scrollHeight}px`;
    }
  }, [comment, loading]);

  // sync dateClose when status changes
  useEffect(() => {
    if (status === 'Отработано') {
      if (!dateClose) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        setDateClose(`${yyyy}-${mm}-${dd}`);
      }
    } else {
      setDateClose('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // save function (without navigation)
  const saveChanges = async () => {
    const payload = {
      collector_id: site,
      reason: reason,
      comments: comment,
      status: status,
      materials: materials,
      master_name: master,
      closing_date: status === 'Отработано' && dateClose ? dateClose : null,
    };

    try {
      await axios.put(
        `${API_BASE_URL}/breake_problem/update_breake_problem/${id}`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return true;
    } catch (err) {
      console.error('Ошибка при сохранении:', err);
      alert('Не удалось сохранить изменения: ' + (err.response?.data?.message || err.message));
      return false;
    }
  };

  // handle save + navigate to a given path
  const handleSaveAndNavigate = async (pathOrFunc) => {
    const success = await saveChanges();
    if (success) {
      if (typeof pathOrFunc === 'function') {
        pathOrFunc();
      } else {
        navigate(pathOrFunc);
      }
    }
  };

  // handle direct save button
  const handleSave = async () => {
    const success = await saveChanges();
    if (success) {
      navigate(-1);
    }
  };

  // listen for navigation attempts from Sidebar (including logout)
  useEffect(() => {
    const onNavigateAttempt = (e) => {
      const pathOrFunc = e.detail;
      setPendingPath(() => pathOrFunc);
      setShowSaveModal(true);
    };
    window.addEventListener('navigateAttempt', onNavigateAttempt);
    return () => {
      window.removeEventListener('navigateAttempt', onNavigateAttempt);
    };
  }, []);

  if (loading) {
    return <div className="check-details"><p>Загрузка...</p></div>;
  }
  if (error) {
    return (
      <div className="check-details">
        <button className="back-btn" onClick={() => navigate(-1)}>← Назад</button>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="check-details">
      <div className="details-header">
        <button className="back-btn" onClick={() => setShowCancelModal(true)}>
          <span className="back-icon">⌄</span>
        </button>
        <h1 className="page-title">Редактирование поломки</h1>
        <div className="actions">
          <button className="primary-btn cancel" onClick={() => setShowCancelModal(true)}>
            Отменить
          </button>
          <button className="primary-btn save" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>

      <div className="details-container">
        <div className="table-container">
          <div className="table-wrapper">
            <table className="custom-table">
              <colgroup>
                <col style={{ width: '25%' }} />
                <col />
              </colgroup>
              <tbody>
                <tr>
                  <td className="label">Участок</td>
                  <td className="value svet">
                    <Select
                      className="modal-select"
                      classNamePrefix="modal-select"
                      options={siteOptions}
                      value={siteOptions.find((o) => o.value === site) || null}
                      onChange={(opt) => setSite(opt?.value || '')}
                      placeholder="Выберите участок"
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, backgroundColor: '#2a2c33', borderColor: '#3B3B40' }),
                        singleValue: (base) => ({ ...base, color: '#e5e7eb' }),
                        placeholder: (base) => ({ ...base, color: '#8b919d' }),
                        menu: (base) => ({ ...base, backgroundColor: '#2a2c33', borderColor: '#3B3B40' }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                          color: '#e5e7eb',
                        }),
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label">Дата обнаружения</td>
                  <td className="value svet">
                    <input
                      type="date"
                      className="modal-input"
                      value={dateDetect}
                      onChange={(e) => setDateDetect(e.target.value)}
                    />
                  </td>
                </tr>

                {status === 'Отработано' && (
                  <tr>
                    <td className="label">Дата закрытия</td>
                    <td className="value svet">
                      <input
                        type="date"
                        className="modal-input"
                        value={dateClose}
                        onChange={(e) => setDateClose(e.target.value)}
                      />
                    </td>
                  </tr>
                )}

                <tr>
                  <td className="label">Причина поломки</td>
                  <td className="value svet">
                    <Select
                      className="modal-select"
                      classNamePrefix="modal-select"
                      options={reasonOptions}
                      value={reasonOptions.find((o) => o.value === reason) || null}
                      onChange={(opt) => setReason(opt?.value || '')}
                      placeholder="Выберите причину"
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, backgroundColor: '#2a2c33', borderColor: '#3B3B40' }),
                        singleValue: (base) => ({ ...base, color: '#e5e7eb' }),
                        placeholder: (base) => ({ ...base, color: '#8b919d' }),
                        menu: (base) => ({ ...base, backgroundColor: '#2a2c33', borderColor: '#3B3B40' }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                          color: '#e5e7eb',
                        }),
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label">Материалы</td>
                  <td className="edit-house-table__cell edit-house-table__cell--value svet">
                    <input
                      type="text"
                      className="edit-house-input"
                      value={materials}
                      onChange={(e) => setMaterials(e.target.value)}
                      placeholder="—"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label">Исполнитель</td>
                  <td className="edit-house-table__cell edit-house-table__cell--value svet">
                    <input
                      type="text"
                      className="edit-house-input"
                      value={master}
                      onChange={(e) => setMaster(e.target.value)}
                      placeholder="—"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label">Статус</td>
                  <td className="value svet">
                    <Select
                      className="modal-select"
                      classNamePrefix="modal-select"
                      options={[
                        { value: 'Отработано', label: 'Отработано' },
                        { value: 'В процессе', label: 'В процессе' },
                      ]}
                      value={status ? { value: status, label: status } : null}
                      onChange={(opt) => setStatus(opt?.value || '')}
                      placeholder="Выберите статус"
                      menuPortalTarget={document.body}
                      menuPosition="absolute"
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999, width: 240 }),
                        control: (base) => ({
                          ...base,
                          backgroundColor: '#2a2c33',
                          borderColor: '#3B3B40',
                          minHeight: 40,
                          width: 240,
                        }),
                        singleValue: (base) => ({ ...base, color: '#e5e7eb' }),
                        placeholder: (base) => ({ ...base, color: '#8b919d' }),
                        menu: (base) => ({
                          ...base,
                          backgroundColor: '#2a2c33',
                          borderColor: '#3B3B40',
                          maxHeight: 160,
                        }),
                        option: (base, state) => ({
                          ...base,
                          backgroundColor: state.isFocused ? '#3B3B40' : '#2a2c33',
                          color: '#e5e7eb',
                          height: 48,
                        }),
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <td className="label">Комментарий</td>
                  <td className="value comment-cell">
                    <textarea
                      ref={commentRef}
                      className="comment-autogrow"
                      rows={1}
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCancelModal && (
        <CancelEditingModal
          onClose={() => setShowCancelModal(false)}
          onConfirm={() => {
            setShowCancelModal(false);
            navigate(-1);
          }}
        />
      )}

      {showSaveModal && (
        <SaveChangesModal
          onClose={() => {
            setShowSaveModal(false);
            setPendingPath(null);
          }}
          onConfirm={async () => {
            setShowSaveModal(false);
            if (pendingPath) {
              await handleSaveAndNavigate(pendingPath);
              setPendingPath(null);
            }
          }}
          onCancel={() => {
            setShowSaveModal(false);
            setPendingPath(null);
          }}
        />
      )}
    </div>
  );
}
